import { directSupabase } from '../lib/supabaseClient';
import {
  buildSupplierMatches,
  resolvePhoneIdentity,
  type DirectoryCustomerMatch,
  type IdentityIndex,
  type ResolvedIdentity,
  type StagedContactMatch,
} from './identity';
import { rowToContact } from './contacts.model';

function asCustomer(row: Record<string, unknown>): DirectoryCustomerMatch {
  return {
    id: String(row.id),
    name: String(row.name || '').trim() || 'Customer',
    firstName: row.first_name ? String(row.first_name) : null,
    lastName: row.last_name ? String(row.last_name) : null,
    phoneNumber: row.phone_number ? String(row.phone_number) : null,
    phoneNormalized: row.phone_normalized ? String(row.phone_normalized) : null,
    landlineNumber: row.landline_number ? String(row.landline_number) : null,
  };
}

function asStaged(row: Record<string, unknown>): StagedContactMatch {
  const contact = rowToContact(row);
  return {
    id: contact.id,
    displayName: contact.displayName,
    firstName: contact.firstName,
    lastName: contact.lastName,
    phoneOriginal: contact.phoneOriginal,
    landlineOriginal: contact.landlineOriginal,
    phoneNormalized: contact.phoneNormalized,
    landlineNormalized: contact.landlineNormalized,
    customerId: contact.customerId,
    status: contact.status,
    deletedAt: contact.deletedAt,
  };
}

const CUSTOMER_IDENTITY_COLUMNS =
  'id, name, first_name, last_name, phone_number, phone_normalized, landline_number';
const CUSTOMER_IDENTITY_COLUMNS_LEGACY =
  'id, name, first_name, last_name, phone_number, landline_number';

function missingRelation(error: { message?: string; code?: string } | null, name: string): boolean {
  const text = String(error?.message || '');
  return error?.code === '42P01' || text.includes(name);
}

export async function loadIdentityIndex(): Promise<IdentityIndex> {
  const [customersResult, contactsResult, suppliersResult, supplierPhonesResult] = await Promise.all([
    directSupabase
      .from('customers')
      .select(CUSTOMER_IDENTITY_COLUMNS),
    directSupabase
      .from('jarvis_contacts')
      .select('*')
      .is('deleted_at', null),
    directSupabase
      .from('suppliers')
      .select('id, supplier_name, phone'),
    directSupabase
      .from('supplier_phones')
      .select('supplier_id, phone_original, phone_normalized'),
  ]);

  const customersLoad = customersResult.error && /phone_normalized/i.test(customersResult.error.message || '')
    ? await directSupabase.from('customers').select(CUSTOMER_IDENTITY_COLUMNS_LEGACY)
    : customersResult;

  if (customersLoad.error) throw new Error(customersLoad.error.message);

  if (contactsResult.error && !missingRelation(contactsResult.error, 'jarvis_contacts')) {
    throw new Error(contactsResult.error.message);
  }
  const contacts = contactsResult.error
    ? []
    : (contactsResult.data || []).map((row) => asStaged(row as Record<string, unknown>));

  const suppliers = suppliersResult.error
    ? []
    : buildSupplierMatches(
      (suppliersResult.data || []) as Array<{ id: string; supplier_name?: string | null; phone?: string | null }>,
      supplierPhonesResult.error && missingRelation(supplierPhonesResult.error, 'supplier_phones')
        ? []
        : ((supplierPhonesResult.data || []) as Array<{
          supplier_id: string;
          phone_original?: string | null;
          phone_normalized?: string | null;
        }>),
    );

  return {
    customers: (customersLoad.data || []).map((row) => asCustomer(row as Record<string, unknown>)),
    contacts,
    suppliers,
  };
}

export function resolveRecordingPhone(
  index: IdentityIndex,
  phone?: string | null,
  fallbackName?: string | null,
): ResolvedIdentity {
  return resolvePhoneIdentity(index, phone, fallbackName);
}
